import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { isMultiDomains } from 'utils/isMultiDomain';
import config from 'utils/config';
import { getUniqueNonNullOwningTeams } from 'components/FormFields/OwningTeamField/utils';
import { playlistQueryKeys, useUpdatePlaylists, useUpdatePlaylistsAudiences, useUpdatePlaylistsDistributions } from 'actions/playlists';
import { setAlert, setSuccessAlert } from 'actions/alerts';
import { EditorPageStickySaveDiscardButton } from 'components/EditorPageStickyFooter';
import { DistributionField, PlaylistAudienceField, PlaylistOwningTeamField, MemberContributionsField } from 'components/FormFields';
import FormStack from 'components/FormStack';
import { DefaultFormFieldLayout } from 'components/FormField';
import Stack from 'components/Stack';
import TitleCard from 'components/TitleCard';
import UnsavedChangesPrompt from 'components/UnsavedChangesPrompt';
import usePlaylistAccessForm from './utils/usePlaylistAccessForm';
import usePlaylistDomainsForm from './utils/usePlaylistDomainsForm';
import checkForOwningTeamError from './utils/checkForOwningTeamError';
import useComputePlaylistsAudienceAndTeams from './utils/useComputePlaylistsAudienceAndTeams';
import EditingPermissionsSummary from './EditingPermissionsSummary';
import AudienceSummary, { useAudienceSummary } from './AudienceSummary';
function SinglePlaylistAccessPage(_ref) {
  var playlistId = _ref.playlistId,
    accessData = _ref.accessData;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var queryClient = useQueryClient();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isSaving = _useState2[0],
    setIsSaving = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasOwningTeamError = _useState4[0],
    setHasOwningTeamError = _useState4[1];
  var isDeleted = accessData.isDeleted;
  var isDistributed = isMultiDomains([accessData.domain_id]);

  // We're memoising this to create a stable array that doesn't change on every render
  var singlePlaylistAccessData = useMemo(function () {
    return [accessData];
  }, [accessData]);
  var _usePlaylistAccessFor = usePlaylistAccessForm({
      accessData: singlePlaylistAccessData,
      hasOwningTeamError: hasOwningTeamError
    }),
    fields = _usePlaylistAccessFor.fields,
    wasChanged = _usePlaylistAccessFor.wasChanged,
    hasErrors = _usePlaylistAccessFor.hasErrors,
    reset = _usePlaylistAccessFor.reset;
  var _usePlaylistDomainsFo = usePlaylistDomainsForm(),
    domainFields = _usePlaylistDomainsFo.fields,
    resetDomains = _usePlaylistDomainsFo.reset;
  var _useComputePlaylistsA = useComputePlaylistsAudienceAndTeams({
      allowAddingExistingItems: false,
      accessDataItems: [accessData],
      changedAudience: fields.audience.changedValue
    }),
    selectedAudiencesAndTeams = _useComputePlaylistsA.selectedAudiencesAndTeams,
    teamIdsToAdd = _useComputePlaylistsA.teamIdsToAdd,
    teamIdsToRemove = _useComputePlaylistsA.teamIdsToRemove,
    addItem = _useComputePlaylistsA.addItem,
    removeItem = _useComputePlaylistsA.removeItem,
    resetTeamsAndAudience = _useComputePlaylistsA.reset,
    wereTeamsOrAudienceChanged = _useComputePlaylistsA.wasChanged;
  var teamsFromTeamPlaylists = selectedAudiencesAndTeams.map(function (item) {
    return item.filter(function (i) {
      return i.audience === 'teams';
    }).map(function (i) {
      return i.team;
    });
  });
  function resetAllForms() {
    reset();
    resetTeamsAndAudience();
    resetDomains();
  }
  var hasChanges = wasChanged || wereTeamsOrAudienceChanged;
  useEffect(function () {
    setHasOwningTeamError(checkForOwningTeamError({
      audience: fields.audience.value,
      ownerTeamIds: fields.ownerTeamIds.value,
      teamIdsToRemove: teamIdsToRemove,
      audienceTeams: selectedAudiencesAndTeams.map(function (item) {
        return item.map(function (i) {
          return i.team;
        });
      })
    }));
  }, [teamIdsToRemove, fields.audience.value, fields.ownerTeamIds.value, selectedAudiencesAndTeams]);
  var _useUpdatePlaylists = useUpdatePlaylists({
      playlistIds: [playlistId],
      showSuccessAlert: false
    }),
    updatePlaylists = _useUpdatePlaylists.updatePlaylists;
  var _useUpdatePlaylistsAu = useUpdatePlaylistsAudiences({
      playlists: [accessData]
    }),
    updatePlaylistsAudiences = _useUpdatePlaylistsAu.updatePlaylistsAudiences;
  var _useUpdatePlaylistsDi = useUpdatePlaylistsDistributions({
      playlists: singlePlaylistAccessData
    }),
    updatePlaylistsDistributions = _useUpdatePlaylistsDi.updatePlaylistsDistributions;
  function handleSubmit(_x) {
    return _handleSubmit.apply(this, arguments);
  }
  function _handleSubmit() {
    _handleSubmit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var promises, playlistUpdatePayload;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            e.preventDefault();
            setIsSaving(true);
            promises = [];
            if (fields.audience.wasChanged || wereTeamsOrAudienceChanged) {
              promises.push(updatePlaylistsAudiences({
                audience: fields.audience.wasChanged && fields.audience.value !== 'mixed' ? fields.audience.value : undefined,
                teamIdsToAdd: teamIdsToAdd,
                teamIdsToRemove: teamIdsToRemove
              }));
            }
            playlistUpdatePayload = {};
            if (fields.ownerTeamIds.wasChanged) {
              playlistUpdatePayload.ownerTeamId = fields.ownerTeamIds.value[0];
            }
            if (fields.allow_member_contributions.wasChanged) {
              playlistUpdatePayload.allow_member_contributions = fields.allow_member_contributions.value;
            }
            Object.keys(playlistUpdatePayload).length && promises.push(updatePlaylists(playlistUpdatePayload));
            if (domainFields.domainsToAdd.wasChanged || domainFields.domainsToRemove.wasChanged) {
              promises.push(updatePlaylistsDistributions({
                domainsToAdd: domainFields.domainsToAdd.value,
                domainsToRemove: domainFields.domainsToRemove.value
              }));
            }
            if (promises.length) {
              _context.next = 12;
              break;
            }
            setIsSaving(false);
            return _context.abrupt("return");
          case 12:
            _context.prev = 12;
            _context.next = 15;
            return Promise.all(promises);
          case 15:
            setSuccessAlert({
              title: t('alert_playlist_access_update_success')
            });
            _context.next = 18;
            return queryClient.invalidateQueries(playlistQueryKeys.allPlaylists);
          case 18:
            resetAllForms();
            _context.next = 26;
            break;
          case 21:
            _context.prev = 21;
            _context.t0 = _context["catch"](12);
            setAlert({
              key: 'playlist-access-error',
              title: t('alert_playlist_access_update_error'),
              message: _context.t0.message
            });
            _context.next = 26;
            return queryClient.invalidateQueries(playlistQueryKeys.allPlaylists);
          case 26:
            setIsSaving(false);
          case 27:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[12, 21]]);
    }));
    return _handleSubmit.apply(this, arguments);
  }
  var owningTeamsIds = getUniqueNonNullOwningTeams(fields.ownerTeamIds.value);
  var audienceSummary = useAudienceSummary(accessData);
  return /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(UnsavedChangesPrompt, {
    hasUnsavedChanges: hasChanges
  }), /*#__PURE__*/React.createElement(Stack, {
    spacing: "m"
  }, /*#__PURE__*/React.createElement(TitleCard, {
    title: t('playlist_access_who_can_see_this_title')
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: "m"
  }, audienceSummary.hasEffectiveAudience && /*#__PURE__*/React.createElement(AudienceSummary, {
    audienceSummary: audienceSummary,
    accessData: accessData
  }), /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: true
  }, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(PlaylistAudienceField, {
    audienceAndTeams: selectedAudiencesAndTeams,
    hasTeamsToRemove: teamIdsToRemove.length > 0,
    autoShare: [accessData.autoShare],
    onChangeAudience: fields.audience.set,
    onChangeTeams: function onChangeTeams(data) {
      if (data.toAdd) {
        addItem(data.toAdd);
      }
      if (data.toRemove) {
        removeItem(data.toRemove);
      }
    }
  }))))), /*#__PURE__*/React.createElement(TitleCard, {
    title: t('playlist_access_who_can_edit_this_title')
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: "m"
  }, /*#__PURE__*/React.createElement(EditingPermissionsSummary, {
    owningTeamsIds: owningTeamsIds,
    hasMemberContributions: fields.allow_member_contributions.value,
    isDistributed: isDistributed
  }), (!isDistributed && config.enableTeamPlaylists || config.memberContributions) && /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: true
  }, /*#__PURE__*/React.createElement(FormStack, null, !isDistributed && config.enableTeamPlaylists && /*#__PURE__*/React.createElement(PlaylistOwningTeamField, {
    values: fields.ownerTeamIds.value,
    defaultOwnerTeamIds: fields.ownerTeamIds.defaultValue,
    audience: fields.audience.value,
    teams: teamsFromTeamPlaylists,
    contentIds: [playlistId],
    onChange: function onChange(ownerTeamIds) {
      return fields.ownerTeamIds.set(ownerTeamIds);
    },
    alert: fields.ownerTeamIds.error
  }), config.memberContributions && /*#__PURE__*/React.createElement(MemberContributionsField, {
    value: fields.allow_member_contributions.value,
    onChange: function onChange() {
      return fields.allow_member_contributions.set(function (prevValue) {
        return !prevValue;
      });
    },
    isDisabled: isDistributed || isDeleted
  }))))), /*#__PURE__*/React.createElement(TitleCard, {
    title: t('playlist_access_distribution_title'),
    description: t('playlist_access_distribution_description')
  }, /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: true
  }, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(DistributionField, {
    playlistIds: [playlistId],
    values: fields.domains.value,
    onChange: function onChange(data) {
      fields.domains.set(data.selectedItems);
      domainFields.domainsToAdd.set(data.toAdd);
      domainFields.domainsToRemove.set(data.toRemove);
    },
    isDisabled: isDeleted
  }))))), /*#__PURE__*/React.createElement(EditorPageStickySaveDiscardButton, {
    hasChanges: hasChanges,
    isDisabled: hasErrors,
    isSaving: isSaving,
    onDiscard: function onDiscard() {
      return resetAllForms();
    }
  }));
}
export default SinglePlaylistAccessPage;
export { default as usePlaylistAccessForm } from './utils/usePlaylistAccessForm';
export { default as usePlaylistDomainsForm } from './utils/usePlaylistDomainsForm';
export { default as checkForOwningTeamError } from './utils/checkForOwningTeamError';
export { default as useComputePlaylistsAudienceAndTeams } from './utils/useComputePlaylistsAudienceAndTeams';